import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Alert,
    Snackbar,
    Typography,
    Box,
    Chip,
    IconButton,
    TablePagination,
    Radio,
    FormControlLabel
} from '@mui/material';
import { Edit as EditIcon, Visibility as ViewIcon, Add as AddIcon, Analytics as AnalyticsIcon, Extend as ExtendIcon, Close as CloseIcon, Search } from '@mui/icons-material';
import api from '../../services/api';
import { usePermission } from '../../hooks/usePermission';
import './AdminSubscriptionManagement.css';

// Admin subscription UI uses "tokens"; subscription APIs and payloads still use credit* fields and /credits routes.
const AdminSubscriptionManagement = () => {
    const { checkPermission } = usePermission();
    const [subscriptions, setSubscriptions] = useState([]);
    const [dashboardData, setDashboardData] = useState({});
    const [loading, setLoading] = useState(true);
    const [showCreditModal, setShowCreditModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    
    // Pagination state - using same pattern as FeedbackManagement.js
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    
    // Search and filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [searchLoading, setSearchLoading] = useState(false); // Loading state for search button
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });

    // Form for POST .../credits (amount/reason); labels in the modal say "tokens".
    const [creditForm, setCreditForm] = useState({
        credits: '',
        operation: 'add', // 'add' or 'deduct'
        reason: ''
    });

    // Subscription assignment form
    const [assignForm, setAssignForm] = useState({
        merchantSearch: '',
        selectedMerchant: null,
        selectedPackage: null,
        customCredits: '',
        customValidity: '',
        notes: ''
    });

    // Data for assignment
    const [merchants, setMerchants] = useState([]);
    const [packages, setPackages] = useState([]);
    const [searchingMerchants, setSearchingMerchants] = useState(false);

    useEffect(() => {
        loadData();
        loadPackages();
    }, [page, rowsPerPage]);

    const loadData = async () => {
        try {
            setLoading(true);
            
            // Load subscriptions - using same pagination pattern as FeedbackManagement.js
            const subscriptionsResponse = await api.get('/subscription/subscriptions', {
                params: {
                    page: page + 1, // Backend expects 1-based page numbers
                    per_page: rowsPerPage,
                    search: searchTerm,
                    status: filterStatus
                }
            });
            
            setSubscriptions(subscriptionsResponse.data.subscriptions);
            setTotalCount(subscriptionsResponse.data.pagination.total || 0);
            
            // Build user subscriptions map to track latest expired subscriptions
            const userMap = {};
            subscriptionsResponse.data.subscriptions.forEach(sub => {
                if (!userMap[sub.user_id]) {
                    userMap[sub.user_id] = [];
                }
                userMap[sub.user_id].push(sub);
            });
            setUserSubscriptionsMap(userMap);
            
            // Load dashboard data
            const dashboardResponse = await api.get('/subscription/dashboard');
            setDashboardData(dashboardResponse.data);
            
        } catch (error) {
            console.error('Error loading subscriptions:', error);
            showAlert('Error loading subscriptions', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Search handler function - only searches when button is clicked
    const handleSearchSubscriptions = async () => {
        setSearchLoading(true);
        // Reset to first page when searching
        setPage(0);
        try {
            // Load subscriptions with current search term and status filter
            await loadData();
        } finally {
            setSearchLoading(false);
        }
    };

    // Handle page change - same pattern as FeedbackManagement.js
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        // Load specific page when pagination changes
        loadData();
    };

    // Handle rows per page change - same pattern as FeedbackManagement.js
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        // Load first page when rows per page changes
        loadData();
    };

    const loadPackages = async () => {
        try {
            // Only load active packages for subscription assignment
            // Exclude starter packages (package_type='FREE') as they are automatically assigned on registration
            const response = await api.get('/subscription/packages', {
                params: {
                    is_active: 'true',  // Filter to only show active packages
                    exclude_starter: 'true'  // Exclude starter packages from assignment dropdown
                }
            });
            setPackages(response.data.packages);
        } catch (error) {
            console.error('Error loading packages:', error);
        }
    };

    const searchMerchants = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 3) {
            setMerchants([]);
            return;
        }

        try {
            setSearchingMerchants(true);
            // This API endpoint now properly filters to only return actual merchants
            // (users with roles in the Merchants department), excluding admin users
            const response = await api.get('/subscription/users/merchants', {
                params: { search: searchTerm, limit: 10 }
            });
            setMerchants(response.data.users || []);
        } catch (error) {
            console.error('Error searching merchants:', error);
            setMerchants([]);
        } finally {
            setSearchingMerchants(false);
        }
    };

    const handleCreditAdjustment = async (e) => {
        e.preventDefault();
        
        try {
            const payload = {
                amount: parseInt(creditForm.credits),
                action: creditForm.operation === 'deduct' ? 'remove' : creditForm.operation,
                reason: creditForm.reason
            };

            console.log('🔍 [Token adjustment /credits API] Payload:', payload);
            console.log('🔍 [Token adjustment /credits API] User ID:', selectedSubscription.user_id);
            console.log('🔍 [Token adjustment /credits API] Endpoint:', `/subscription/users/${selectedSubscription.user_id}/credits`);

            await api.post(`/subscription/users/${selectedSubscription.user_id}/credits`, payload);
            
            showAlert(`Tokens ${creditForm.operation === 'add' ? 'added' : 'deducted'} successfully`);
            setShowCreditModal(false);
            resetCreditForm();
            loadData();
            
        } catch (error) {
            console.error('Error adjusting balance (API /credits):', error);
            // Prefer server message (may still say "credit"); fallback uses token wording for admins.
            showAlert(error.response?.data?.error || 'Error adjusting tokens', 'error');
        }
    };

    const handleSubscriptionAction = async (subscriptionId, action) => {
        try {
            let response;
            if (action === 'suspend') {
                response = await api.post(`/subscription/subscriptions/${subscriptionId}/suspend`, {
                    reason: 'Admin suspension'
                });
            } else if (action === 'activate') {
                response = await api.post(`/subscription/subscriptions/${subscriptionId}/activate`);
            }
            
            if (response && response.data) {
                showAlert(`Subscription ${action}ed successfully`, 'success');
                loadData();
            }
        } catch (error) {
            console.error(`Error ${action}ing subscription:`, error);
            showAlert(`Error ${action}ing subscription`, 'error');
        }
    };

    const handleExtendSubscription = async (subscription) => {
        try {
            setIsExtending(true);
            const payload = {
                reason: extendForm.reason
            };

            if (extendForm.extensionType === 'days') {
                payload.additional_days = extendForm.additional_days;
            } else {
                payload.new_expiry_date = extendForm.new_expiry_date;
            }

            const response = await api.post(`/subscription/subscriptions/${subscription.id}/extend`, payload);
            
            if (response && response.data) {
                const expiryDate = formatDate(extendForm.extensionType === 'days' 
                    ? calculateNewExpiryDate(subscription) 
                    : extendForm.new_expiry_date);
                showAlert(`Subscription extended successfully. New expiry: ${expiryDate}`, 'success');
                loadData();
            }
        } catch (error) {
            console.error('Error extending subscription:', error);
            showAlert(error.response?.data?.error || 'Error extending subscription', 'error');
        } finally {
            setIsExtending(false);
        }
    };

    const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [extendForm, setExtendForm] = useState({
        extensionType: 'days', // 'days' or 'date'
        additional_days: 30,
        new_expiry_date: '',
        reason: 'Admin extension'
    });
    const [selectedSubscriptionForExtend, setSelectedSubscriptionForExtend] = useState(null);
    const [userSubscriptionsMap, setUserSubscriptionsMap] = useState({}); // Track subscriptions per user
    const [isExtending, setIsExtending] = useState(false); // Loading state for extend operation

    const handleViewAnalytics = async () => {
        try {
            console.log('🔍 [Analytics] Calling API...');
            const response = await api.get('/subscription/analytics');
            console.log('🔍 [Analytics] API Response:', response);
            console.log('🔍 [Analytics] Response Data:', response.data);
            
            if (response.data) {
                setAnalyticsData(response.data);
                setShowAnalyticsModal(true);
                console.log('🔍 [Analytics] Modal opened with data:', response.data);
            } else {
                console.log('🔍 [Analytics] No data in response');
                showAlert('No analytics data received', 'warning');
            }
        } catch (error) {
            console.error('❌ [Analytics] Error loading analytics:', error);
            showAlert('Error loading analytics data', 'error');
        }
    };

    const handleUpdateStatuses = async () => {
        try {
            console.log('🔍 [Status Update] Calling API...');
            const response = await api.post('/subscription/update-statuses');
            console.log('🔍 [Status Update] API Response:', response);
            
            if (response.data && response.data.success) {
                showAlert(`Successfully updated ${response.data.updated_count} subscription statuses`, 'success');
                // Reload data to show updated statuses
                loadData();
            } else {
                showAlert('Failed to update subscription statuses', 'error');
            }
        } catch (error) {
            console.error('❌ [Status Update] Error updating statuses:', error);
            showAlert(error.response?.data?.error || 'Error updating subscription statuses', 'error');
        }
    };

    const openCreditModal = (subscription) => {
        setSelectedSubscription(subscription);
        setShowCreditModal(true);
    };

    const resetCreditForm = () => {
        setCreditForm({
            credits: '',
            operation: 'add',
            reason: ''
        });
        setSelectedSubscription(null);
    };

    const handleAssignSubscription = async (e) => {
        e.preventDefault();
        
        if (!assignForm.selectedMerchant || !assignForm.selectedPackage) {
            showAlert('Please select both merchant and package', 'error');
            return;
        }

        try {
            const payload = {
                user_id: assignForm.selectedMerchant.id,
                package_id: assignForm.selectedPackage.id,
                custom_credits: assignForm.customCredits || null,
                custom_validity: assignForm.customValidity || null,
                notes: assignForm.notes || '',
                assigned_by: 'admin' // This will be set from JWT in backend
            };

            const response = await api.post('/subscription/assign', payload);
            const data = response.data;
            
            // Create detailed success message
            let successMessage = `Subscription assigned successfully to ${assignForm.selectedMerchant.name}`;
            
            if (data.carried_forward_credits > 0) {
                successMessage += `\n• ${data.carried_forward_credits} tokens carried forward from previous subscription`;
            }
            
            if (data.new_credits > 0) {
                successMessage += `\n• ${data.new_credits} new tokens from ${data.package_name}`;
            }
            
            successMessage += `\n• Total tokens: ${data.total_credits}`;
            // Expiry line uses DD/MM/YYYY in IST for consistency with subscription tables
            successMessage += `\n• Expires: ${new Date(data.expiry_date).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: 'Asia/Kolkata'
            })}`;
            
            if (data.previous_subscription_id) {
                successMessage += `\n• Previous subscription: #${data.previous_subscription_id}`;
            }
            
            showAlert(successMessage);
            setShowAssignModal(false);
            resetAssignForm();
            loadData();
            
        } catch (error) {
            console.error('Error assigning subscription:', error);
            showAlert(error.response?.data?.error || 'Error assigning subscription', 'error');
        }
    };

    const resetAssignForm = () => {
        setAssignForm({
            merchantSearch: '',
            selectedMerchant: null,
            selectedPackage: null,
            customCredits: '',
            customValidity: '',
            notes: ''
        });
        setMerchants([]);
    };

    // Helper function to check if subscription is the latest expired for user
    const isLatestExpiredSubscription = (subscription) => {
        if (subscription.status !== 'EXPIRED') return false;
        
        const userSubs = userSubscriptionsMap[subscription.user_id] || [];
        if (userSubs.length === 0) return true;
        
        // Check if user has any active subscriptions
        const hasActive = userSubs.some(sub => sub.status === 'ACTIVE');
        if (hasActive) return false;
        
        // Find the latest expired subscription (most recent created_at or expiry date)
        const expiredSubs = userSubs.filter(sub => sub.status === 'EXPIRED');
        if (expiredSubs.length === 0) return true;
        
        // Sort by created_at descending to get the latest
        expiredSubs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        // Check if this is the latest expired
        return expiredSubs[0].id === subscription.id;
    };

    // Helper function to calculate new expiry date
    const calculateNewExpiryDate = (subscription) => {
        if (!subscription) return null;
        
        if (extendForm.extensionType === 'days') {
            const currentExpiry = new Date(subscription.package_expiry_date);
            const newExpiry = new Date(currentExpiry);
            newExpiry.setDate(newExpiry.getDate() + (extendForm.additional_days || 0));
            return newExpiry;
        } else {
            return extendForm.new_expiry_date ? new Date(extendForm.new_expiry_date) : null;
        }
    };

    const showAlert = (message, type = 'success') => {
        setAlert({ show: true, message, type });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        // en-GB + IST: DD/MM/YYYY (matches other admin screens; avoids local-TZ drift from getDate/getMonth)
        return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: 'Asia/Kolkata'
        });
    };

    // Locale number formatting only; surrounding UI labels use "tokens".
    const formatCredits = (credits) => {
        return new Intl.NumberFormat('en-IN').format(credits);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'ACTIVE': { color: 'success', label: 'Active' },
            'EXPIRED': { color: 'warning', label: 'Expired' },
            'CANCELLED': { color: 'error', label: 'Cancelled' },
            'SUSPENDED': { color: 'default', label: 'Suspended' }
        };
        const config = statusMap[status] || { color: 'default', label: status };
        return <Chip label={config.label} color={config.color} />;
    };

    return (
        <div className="admin-subscription-container">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h2">
                    Subscription Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {checkPermission('Subscription Managament', 'view_analytics') && (
                        <Button 
                            variant="outlined" 
                            onClick={() => handleViewAnalytics()}
                            startIcon={<AnalyticsIcon />}
                        >
                            Analytics
                        </Button>
                    )}
                    {checkPermission('Subscription Managament', 'manage_subscriptions') && (
                        <Button 
                            variant="outlined" 
                            onClick={() => handleUpdateStatuses()}
                            startIcon={<EditIcon />}
                            color="warning"
                        >
                            Update Statuses
                        </Button>
                    )}
                    {checkPermission('Subscription Managament', 'manage_subscriptions') && (
                        <Button 
                            variant="contained" 
                            onClick={() => setShowAssignModal(true)}
                            startIcon={<AddIcon />}
                        >
                            Assign Subscription
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Dashboard Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Subscriptions
                            </Typography>
                            <Typography variant="h4" component="h2">
                                {dashboardData.overview?.total_subscriptions || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Active Subscriptions
                            </Typography>
                            <Typography variant="h4" component="h2" color="success.main">
                                {dashboardData.overview?.active_subscriptions || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Expired Subscriptions
                            </Typography>
                            <Typography variant="h4" component="h2" color="warning.main">
                                {dashboardData.overview?.expired_subscriptions || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Packages
                            </Typography>
                            <Typography variant="h4" component="h2" color="primary.main">
                                {dashboardData.overview?.total_packages || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={5} md={5}>
                            <TextField
                                fullWidth
                                label="Search users or packages..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                variant="outlined"
                                size="medium"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '48px',
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={4}>
                            <FormControl fullWidth variant="outlined" size="medium">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    label="Status"
                                    sx={{
                                        height: '48px',
                                    }}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="ACTIVE">Active</MenuItem>
                                    <MenuItem value="EXPIRED">Expired</MenuItem>
                                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                                    <MenuItem value="SUSPENDED">Suspended</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3} md={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                startIcon={<Search />}
                                onClick={handleSearchSubscriptions}
                                disabled={searchLoading}
                                size="large"
                                sx={{ 
                                    height: '48px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.95rem'
                                }}
                            >
                                {searchLoading ? 'Searching...' : 'Search'}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Subscriptions Table - using same pattern as FeedbackManagement.js */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Package</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="right">Tokens</TableCell>
                                <TableCell align="center">Package Expiry</TableCell>
                                <TableCell align="center">Token Expiry</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography>Loading subscriptions...</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : subscriptions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No subscriptions found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                subscriptions.map((subscription) => (
                                    <TableRow key={subscription.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2">
                                                {subscription.user_name || 'Unknown User'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {subscription.user_email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="subtitle2">
                                                {subscription.package_name}
                                            </Typography>
                                            <Chip 
                                                label={subscription.package_type} 
                                                size="small" 
                                                variant="outlined" 
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {getStatusBadge(subscription.status)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="h6" color="primary">
                                                {formatCredits(subscription.remaining_credits)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                of {formatCredits(subscription.initial_credits)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {formatDate(subscription.package_expiry_date)}
                                            </Typography>
                                            {/* Show "Expiring Soon" only if status is not EXPIRED and days_until_package_expiry is 7 or less */}
                                            {subscription.status !== 'EXPIRED' && subscription.days_until_package_expiry <= 7 && (
                                                <Chip label="Expiring Soon" color="warning" size="small" />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {formatDate(subscription.credit_expiry_date)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                gap: 0.5,
                                                flexDirection: 'row',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {checkPermission('Subscription Managament', 'adjust_credits') && subscription.status !== 'EXPIRED' && (
                                                    <IconButton 
                                                        color="primary"
                                                        onClick={() => openCreditModal(subscription)}
                                                        size="small"
                                                        title="Adjust tokens"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                )}
                                                {checkPermission('Subscription Managament', 'suspend_subscription') && subscription.status === 'ACTIVE' && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="warning"
                                                        onClick={() => handleSubscriptionAction(subscription.id, 'suspend')}
                                                        sx={{ minWidth: 'auto', px: 1 }}
                                                    >
                                                        Suspend
                                                    </Button>
                                                )}
                                                {checkPermission('Subscription Managament', 'manage_subscriptions') && subscription.status === 'SUSPENDED' && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="success"
                                                        onClick={() => handleSubscriptionAction(subscription.id, 'activate')}
                                                        sx={{ minWidth: 'auto', px: 1 }}
                                                    >
                                                        Activate
                                                    </Button>
                                                )}
                                                {checkPermission('Subscription Managament', 'extend_subscription') && 
                                                 (subscription.status === 'ACTIVE' || isLatestExpiredSubscription(subscription)) && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                        onClick={() => {
                                                            setSelectedSubscriptionForExtend(subscription);
                                                            // Reset form when opening modal
                                                            setExtendForm({
                                                                extensionType: 'days',
                                                                additional_days: 30,
                                                                new_expiry_date: '',
                                                                reason: 'Admin extension'
                                                            });
                                                            setShowExtendModal(true);
                                                        }}
                                                        sx={{ minWidth: 'auto', px: 1 }}
                                                    >
                                                        Extend
                                                    </Button>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination - using same pattern as FeedbackManagement.js */}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Extend Subscription Modal */}
            <Dialog open={showExtendModal} onClose={() => setShowExtendModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Extend Subscription
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowExtendModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedSubscriptionForExtend && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {selectedSubscriptionForExtend.status === 'EXPIRED' ? 'Reviving expired' : 'Extending active'} subscription for: <strong>{selectedSubscriptionForExtend.user_name}</strong>
                            </Typography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Current Package Expiry: <strong>{formatDate(selectedSubscriptionForExtend.package_expiry_date)}</strong>
                                    </Typography>
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Extension Type</Typography>
                                    <FormControl component="fieldset">
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <FormControlLabel
                                                control={
                                                    <Radio
                                                        checked={extendForm.extensionType === 'days'}
                                                        onChange={(e) => setExtendForm({...extendForm, extensionType: 'days'})}
                                                    />
                                                }
                                                label="Add Days"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Radio
                                                        checked={extendForm.extensionType === 'date'}
                                                        onChange={(e) => setExtendForm({...extendForm, extensionType: 'date'})}
                                                    />
                                                }
                                                label="Set Specific Date"
                                            />
                                        </Box>
                                    </FormControl>
                                </Grid>
                                
                                {extendForm.extensionType === 'days' ? (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Additional Days"
                                            type="number"
                                            value={extendForm.additional_days}
                                            onChange={(e) => setExtendForm({...extendForm, additional_days: parseInt(e.target.value) || 0})}
                                            inputProps={{ min: 1, max: 365 }}
                                            helperText="Number of days to extend the subscription"
                                        />
                                    </Grid>
                                ) : (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="New Expiry Date"
                                            type="date"
                                            value={extendForm.new_expiry_date}
                                            onChange={(e) => setExtendForm({...extendForm, new_expiry_date: e.target.value})}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                            helperText="Select the new expiry date for the subscription"
                                        />
                                    </Grid>
                                )}
                                
                                {/* Show calculated new expiry date */}
                                {extendForm.extensionType === 'days' && extendForm.additional_days > 0 && (
                                    <Grid item xs={12}>
                                        <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                New Package Expiry: <strong>{formatDate(calculateNewExpiryDate(selectedSubscriptionForExtend))}</strong>
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                
                                {extendForm.extensionType === 'date' && extendForm.new_expiry_date && (
                                    <Grid item xs={12}>
                                        <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                New Package Expiry: <strong>{formatDate(extendForm.new_expiry_date)}</strong>
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Reason"
                                        value={extendForm.reason}
                                        onChange={(e) => setExtendForm({...extendForm, reason: e.target.value})}
                                        multiline
                                        rows={2}
                                        helperText="Reason for extending the subscription"
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowExtendModal(false)} disabled={isExtending}>Cancel</Button>
                    <Button 
                        onClick={async () => {
                            if (selectedSubscriptionForExtend) {
                                await handleExtendSubscription(selectedSubscriptionForExtend);
                                setShowExtendModal(false);
                                setSelectedSubscriptionForExtend(null);
                            }
                        }}
                        variant="contained"
                        color="primary"
                        disabled={isExtending || 
                                   (extendForm.extensionType === 'days' && (!extendForm.additional_days || extendForm.additional_days <= 0)) || 
                                   (extendForm.extensionType === 'date' && !extendForm.new_expiry_date)}
                    >
                        {isExtending ? 'Extending...' : 'Extend Subscription'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Analytics Modal */}
            <Dialog open={showAnalyticsModal} onClose={() => setShowAnalyticsModal(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Subscription Analytics
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowAnalyticsModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {analyticsData ? (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>Package Distribution</Typography>
                            {analyticsData.package_stats && (
                                <TableContainer component={Paper} sx={{ mb: 3 }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Package Name</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell align="right">Subscriptions</TableCell>
                                                <TableCell align="right">Total Tokens</TableCell>
                                                <TableCell align="right">Remaining Tokens</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {analyticsData.package_stats.map((pkg, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{pkg.name}</TableCell>
                                                    <TableCell>{pkg.package_type}</TableCell>
                                                    <TableCell align="right">{pkg.subscription_count}</TableCell>
                                                    <TableCell align="right">{pkg.total_credits_allocated}</TableCell>
                                                    <TableCell align="right">{pkg.total_credits_remaining}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                            
                            {/* Revenue Statistics */}
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Revenue Statistics</Typography>
                            {analyticsData.revenue_stats && (
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={4}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Total Revenue
                                                </Typography>
                                                <Typography variant="h4" component="h2">
                                                    ₹{analyticsData.revenue_stats.total_revenue || 0}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Monthly Recurring Revenue
                                                </Typography>
                                                <Typography variant="h4" component="h2">
                                                    ₹{analyticsData.revenue_stats.monthly_recurring_revenue || 0}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Average Revenue per User
                                                </Typography>
                                                <Typography variant="h4" component="h2">
                                                    ₹{analyticsData.revenue_stats.average_revenue_per_user || 0}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}

                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Subscription Trends</Typography>
                            {analyticsData.subscription_trends && (
                                <TableContainer component={Paper} sx={{ mb: 3 }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>New Subscriptions</TableCell>
                                                <TableCell>Renewal Type</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {analyticsData.subscription_trends.slice(0, 10).map((trend, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{formatDate(trend.date)}</TableCell>
                                                    <TableCell>{trend.new_subscriptions}</TableCell>
                                                    <TableCell>{trend.renewal_type}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {/* Debug Information */}
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Debug Information</Typography>
                            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Raw data structure received:
                                </Typography>
                                <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                                    {JSON.stringify(analyticsData, null, 2)}
                                </pre>
                            </Box>
                        </Box>
                    ) : (
                        <Typography>Loading analytics...</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowAnalyticsModal(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Token adjustment; API: POST .../users/:id/credits */}
            <Dialog open={showCreditModal} onClose={() => setShowCreditModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Adjust tokens for {selectedSubscription?.user_name}
                </DialogTitle>
                <form onSubmit={handleCreditAdjustment}>
                    <DialogContent>
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Current tokens: {formatCredits(selectedSubscription?.remaining_credits || 0)}
                            </Typography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Operation</InputLabel>
                                        <Select
                                            value={creditForm.operation}
                                            onChange={(e) => setCreditForm({...creditForm, operation: e.target.value})}
                                            label="Operation"
                                        >
                                            <MenuItem value="add">Add tokens</MenuItem>
                                            <MenuItem value="deduct">Deduct tokens</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Token amount"
                                        type="number"
                                        value={creditForm.credits}
                                        onChange={(e) => setCreditForm({...creditForm, credits: e.target.value})}
                                        required
                                        inputProps={{ min: 1 }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Reason"
                                        multiline
                                        rows={3}
                                        value={creditForm.reason}
                                        onChange={(e) => setCreditForm({...creditForm, reason: e.target.value})}
                                        required
                                        placeholder="Please provide a reason for this token adjustment..."
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowCreditModal(false)}>Cancel</Button>
                        <Button type="submit" variant="contained" color={creditForm.operation === 'add' ? 'success' : 'warning'}>
                            {creditForm.operation === 'add' ? 'Add' : 'Deduct'} tokens
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Assign Subscription Modal */}
            <Dialog open={showAssignModal} onClose={() => setShowAssignModal(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Assign Subscription to Merchant
                </DialogTitle>
                <form onSubmit={handleAssignSubscription}>
                    <DialogContent>
                        <Box sx={{ mt: 1 }}>
                            <Grid container spacing={3}>
                                {/* Merchant Search */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                        Select Merchant
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        label="Search merchants by name or email"
                                        value={assignForm.merchantSearch}
                                        onChange={(e) => {
                                            setAssignForm({...assignForm, merchantSearch: e.target.value});
                                            searchMerchants(e.target.value);
                                        }}
                                        placeholder="Type merchant name or email..."
                                    />
                                    
                                    {/* Merchant Results */}
                                    {assignForm.merchantSearch.length >= 3 && (
                                        <Box sx={{ mt: 2, maxHeight: 200, overflowY: 'auto' }}>
                                            {searchingMerchants ? (
                                                <Typography>Searching...</Typography>
                                            ) : merchants.length > 0 ? (
                                                merchants.map((merchant) => (
                                                    <Box
                                                        key={merchant.id}
                                                        sx={{
                                                            p: 2,
                                                            border: '1px solid #e0e0e0',
                                                            borderRadius: 1,
                                                            mb: 1,
                                                            cursor: 'pointer',
                                                            backgroundColor: assignForm.selectedMerchant?.id === merchant.id ? '#e3f2fd' : 'white',
                                                            '&:hover': { backgroundColor: '#f5f5f5' }
                                                        }}
                                                        onClick={() => setAssignForm({
                                                            ...assignForm,
                                                            selectedMerchant: merchant,
                                                            merchantSearch: `${merchant.name} (${merchant.email})`
                                                        })}
                                                    >
                                                        <Typography variant="subtitle2">
                                                            {merchant.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {merchant.email}
                                                        </Typography>
                                                        {merchant.company_name && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Company: {merchant.company_name}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ))
                                            ) : (
                                                <Typography color="text.secondary">
                                                    No merchants found
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    
                                    {/* Selected Merchant */}
                                    {assignForm.selectedMerchant && (
                                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                                            <Typography variant="subtitle2" color="success.main">
                                                Selected: {assignForm.selectedMerchant.name} ({assignForm.selectedMerchant.email})
                                            </Typography>
                                        </Box>
                                    )}
                                </Grid>

                                {/* Package Selection */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                        Select Package
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Only active packages are shown for assignment (starter packages are automatically assigned on registration)
                                    </Typography>
                                    <FormControl fullWidth required>
                                        <InputLabel>Choose Package</InputLabel>
                                        <Select
                                            value={assignForm.selectedPackage?.id || ''}
                                            onChange={(e) => {
                                                const selectedPkg = packages.find(p => p.id === e.target.value);
                                                setAssignForm({...assignForm, selectedPackage: selectedPkg});
                                            }}
                                            label="Choose Package"
                                        >
                                            {packages.map((pkg) => (
                                                <MenuItem key={pkg.id} value={pkg.id}>
                                                    <Box>
                                                        <Typography variant="subtitle2">
                                                            {pkg.name} - {pkg.credits} tokens
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            ₹{pkg.price} • {pkg.validity_days} days
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    
                                    {/* Selected Package Details */}
                                    {assignForm.selectedPackage && (
                                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                                            <Typography variant="subtitle2" color="primary.main">
                                                Package: {assignForm.selectedPackage.name}
                                            </Typography>
                                            <Typography variant="body2">
                                                Tokens: {assignForm.selectedPackage.credits} • 
                                                Price: ₹{assignForm.selectedPackage.price} • 
                                                Validity: {assignForm.selectedPackage.validity_days} days
                                            </Typography>
                                        </Box>
                                    )}
                                </Grid>

                                {/* Custom Overrides (for Enterprise packages) */}
                                {assignForm.selectedPackage?.is_enterprise && (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Custom tokens (optional)"
                                                type="number"
                                                value={assignForm.customCredits}
                                                onChange={(e) => setAssignForm({...assignForm, customCredits: e.target.value})}
                                                placeholder="Leave empty to use package default"
                                                helperText="Override default tokens for this merchant"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Custom Validity Days (optional)"
                                                type="number"
                                                value={assignForm.customValidity}
                                                onChange={(e) => setAssignForm({...assignForm, customValidity: e.target.value})}
                                                placeholder="Leave empty to use package default"
                                                helperText="Override default validity for this merchant"
                                            />
                                        </Grid>
                                    </>
                                )}

                                {/* Notes */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Admin Notes (optional)"
                                        multiline
                                        rows={3}
                                        value={assignForm.notes}
                                        onChange={(e) => setAssignForm({...assignForm, notes: e.target.value})}
                                        placeholder="Any special notes about this assignment..."
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowAssignModal(false)}>Cancel</Button>
                        <Button 
                            type="submit" 
                            variant="contained"
                            disabled={!assignForm.selectedMerchant || !assignForm.selectedPackage}
                        >
                            Assign Subscription
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Alert Snackbar */}
            <Snackbar 
                open={alert.show} 
                autoHideDuration={6000} 
                onClose={() => setAlert({...alert, show: false})}
            >
                <Alert 
                    onClose={() => setAlert({...alert, show: false})} 
                    severity={alert.type}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default AdminSubscriptionManagement; 